import { useState, useRef, useEffect, useCallback, memo } from "react";
import { MessageSquare, Send, X, Loader2, ArrowRight, Sparkles, Mic, MicOff } from "lucide-react";
import { usePlateTalkStore, type PlateTalkMessage } from "@/lib/plate-talk-store";
import { useLocation } from "wouter";
import { useAudioControl } from "@/lib/audio-control-store";

const PAGE_CONTEXT: Record<string, { label: string; hints: string[] }> = {
  "/dashboard": {
    label: "Dashboard",
    hints: ["How am I doing today?", "Analyze my day", "What should I eat next?"],
  },
  "/plate-log": {
    label: "Plate Log",
    hints: ["Plan my remaining meals", "Am I hitting my targets?", "What's missing?"],
  },
  "/foods": {
    label: "Foods",
    hints: ["What foods am I low on?", "Suggest new foods for my goals", "Find high-protein options"],
  },
  "/grocery": {
    label: "Grocery",
    hints: ["What should I buy this week?", "Optimize my grocery list", "Suggest budget-friendly options"],
  },
  "/metrics": {
    label: "Metrics",
    hints: ["Which nutrients am I deficient in?", "Show my worst metrics", "How do I improve?"],
  },
  "/grid": {
    label: "Daily Grid",
    hints: ["Analyze my weekly pattern", "Where are my gaps?", "What day was best?"],
  },
  "/evidence": {
    label: "Evidence Lab",
    hints: ["What does the evidence say?", "Explain this nutrient", "Best food sources for iron?"],
  },
  "/smart-swap": {
    label: "Smart Swap",
    hints: ["Find healthier swaps", "What should I replace?", "Better breakfast options?"],
  },
  "/profile": {
    label: "Profile",
    hints: ["Am I on track for my goals?", "Adjust my targets", "Review my progress"],
  },
  "/glossary": {
    label: "Glossary",
    hints: ["What is magnesium?", "Explain omega-3s", "Why is fiber important?"],
  },
};

const MiniMessage = memo(function MiniMessage({ msg }: { msg: PlateTalkMessage }) {
  const maxLen = 300;
  const text = msg.content.length > maxLen ? msg.content.slice(0, maxLen) + "..." : msg.content;

  const hasActions = msg.actions && msg.actions.length > 0;
  const actionSummary = hasActions
    ? msg.actions!
        .map((a) => {
          if (a.action === "log_foods" || a.action === "log_foods_pending") return `📝 ${a.count ?? a.proposed?.length ?? a.logged?.length ?? 0} foods`;
          if (a.action === "plan_remaining") return `🍽️ Meal plan`;
          if (a.action === "analyze_day") return `📊 Analysis`;
          if (a.action === "smart_adjust") return `🔄 Adjustments`;
          if (a.action === "rearrange_plate") return `🍽️ Plate rearrange`;
          return null;
        })
        .filter(Boolean)
    : [];

  return (
    <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
          msg.role === "user"
            ? "bg-primary/20 text-primary-foreground border border-primary/30"
            : "bg-muted/40 text-foreground border border-border/30"
        }`}
      >
        {msg.streaming && !msg.content ? (
          <div className="flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin text-primary" />
            <span className="text-muted-foreground">Analyzing...</span>
          </div>
        ) : (
          <>
            <p className="whitespace-pre-wrap">{text}</p>
            {actionSummary.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {actionSummary.map((s, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                    {s}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

export function GlobalPlateTalk() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const { messages, state, sendMessage, completedWhileAway, clearCompletedFlag } = usePlateTalkStore();
  const [location, setLocation] = useLocation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const { voiceEnabled, speakText } = useAudioControl();

  const isOnPlateTalk = location === "/plate-talk" || location.startsWith("/plate-talk?");

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        fabRef.current &&
        !fabRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || state !== "idle") return;
    sendMessage(trimmed, null, null, voiceEnabled, voiceEnabled ? (t) => speakText(t) : undefined);
    setInput("");
  }, [input, state, sendMessage, voiceEnabled, speakText]);

  const handleHint = useCallback(
    (hint: string) => {
      if (state !== "idle") return;
      sendMessage(hint, null, null, voiceEnabled, voiceEnabled ? (t) => speakText(t) : undefined);
    },
    [state, sendMessage, voiceEnabled, speakText]
  );

  const handleOpenFull = () => {
    setOpen(false);
    clearCompletedFlag();
    setLocation("/plate-talk");
  };

  if (isOnPlateTalk) return null;

  const ctx = PAGE_CONTEXT[location] || { label: "this page", hints: ["What should I eat?", "Analyze my day", "Plan my meals"] };
  const recentMessages = messages.slice(-6);
  const isActive = state !== "idle";
  const hasNotification = completedWhileAway;

  return (
    <>
      {open && (
        <div
          ref={panelRef}
          className="fixed bottom-20 left-4 right-4 md:left-auto md:right-20 md:bottom-6 z-50 md:w-[380px] rounded-2xl bg-background/98 backdrop-blur-xl border border-border shadow-2xl overflow-hidden flex flex-col"
          style={{
            maxHeight: "min(70vh, 520px)",
            boxShadow: "0 0 40px rgba(0,0,0,0.4), 0 0 20px rgba(var(--glow-rgb), 0.15)",
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-primary/5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground">Plate Talk</p>
                <p className="text-[10px] text-muted-foreground">
                  {isActive ? "Analyzing..." : `Ask about ${ctx.label}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleOpenFull}
                className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted/50 text-[10px] text-primary font-medium transition-colors"
              >
                Full view <ArrowRight className="w-3 h-3" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-muted/50 transition-colors">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[120px]">
            {recentMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-primary/60" />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Ask Plate Talk anything about your nutrition
                </p>
                <div className="flex flex-wrap gap-1.5 justify-center mt-1">
                  {ctx.hints.map((hint, i) => (
                    <button
                      key={i}
                      onClick={() => handleHint(hint)}
                      disabled={isActive}
                      className="text-[10px] px-2.5 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors disabled:opacity-50"
                    >
                      {hint}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {recentMessages.map((msg) => (
                  <MiniMessage key={msg.id} msg={msg} />
                ))}
                {messages.length > 6 && (
                  <button
                    onClick={handleOpenFull}
                    className="w-full text-center text-[10px] text-primary/60 hover:text-primary py-1 transition-colors"
                  >
                    View {messages.length - 6} more messages →
                  </button>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {recentMessages.length > 0 && state === "idle" && (
            <div className="px-3 pb-1">
              <div className="flex flex-wrap gap-1">
                {ctx.hints.slice(0, 2).map((hint, i) => (
                  <button
                    key={i}
                    onClick={() => handleHint(hint)}
                    className="text-[10px] px-2 py-1 rounded-full bg-muted/40 text-muted-foreground hover:bg-muted/60 border border-border/30 transition-colors"
                  >
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="p-3 border-t border-border/50">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder={isActive ? "Analyzing..." : "Ask anything..."}
                disabled={isActive}
                className="flex-1 h-9 px-3 rounded-lg bg-muted/30 border border-border/40 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40 disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isActive}
                className="w-9 h-9 rounded-lg bg-primary/90 hover:bg-primary text-primary-foreground flex items-center justify-center transition-colors disabled:opacity-40"
              >
                {isActive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        ref={fabRef}
        onClick={() => {
          setOpen(!open);
          if (completedWhileAway) clearCompletedFlag();
        }}
        className={`fixed bottom-24 left-4 md:bottom-6 md:left-auto md:right-20 z-40 w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group ${
          isActive
            ? "bg-primary/90 hover:bg-primary"
            : hasNotification
            ? "bg-emerald-500/90 hover:bg-emerald-500"
            : "bg-gradient-to-br from-violet-600 to-primary hover:from-violet-500 hover:to-primary/90"
        } text-white`}
        style={{
          boxShadow: isActive
            ? "0 0 20px rgba(var(--glow-rgb), 0.4)"
            : hasNotification
            ? "0 0 20px rgba(52,211,153,0.4)"
            : "0 0 15px rgba(var(--glow-rgb), 0.25)",
        }}
      >
        {isActive ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <MessageSquare className="w-5 h-5 group-hover:scale-110 transition-transform" />
        )}
        {hasNotification && !open && (
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-background animate-pulse" />
        )}
      </button>
    </>
  );
}
